import { useState } from 'react'
import { createClient } from '@supabase/supabase-js'
import { importHealthData } from './utils/importHealthData'
import { supabase } from './supabaseClient'
import { motion } from 'framer-motion'

// Define the type for weight entries
interface WeightEntry {
  date: string
  weight: number
  active_energy?: number
  body_fat_percentage?: number
  body_mass_index?: number
  cycling_distance?: number
}

// Define database types
interface Database {
  public: {
    Tables: {
      weight_entries: {
        Row: WeightEntry
        Insert: WeightEntry
        Update: Partial<WeightEntry>
      }
    }
  }
}

// Initialize Supabase admin client with service role key
const supabaseAdmin = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_SERVICE_KEY,
  {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    },
    db: {
      schema: 'public'
    }
  }
)

// This should match your environment variable
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage on initial load
    const storedAuth = localStorage.getItem('adminAuth')
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth)
        // Check if the stored session is still valid (7 days)
        if (authData.timestamp && Date.now() - authData.timestamp < 7 * 24 * 60 * 60 * 1000) {
          return true
        } else {
          // Clear expired session
          localStorage.removeItem('adminAuth')
        }
      } catch (e) {
        localStorage.removeItem('adminAuth')
      }
    }
    return false
  })

  const [password, setPassword] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isPooping, setIsPooping] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === ADMIN_PASSWORD) {
      // Store authentication state with timestamp
      localStorage.setItem('adminAuth', JSON.stringify({
        timestamp: Date.now()
      }))
      setIsAuthenticated(true)
      setError(null)
    } else {
      setError('Invalid password')
    }
    setPassword('')
  }

  const handleLogout = () => {
    localStorage.removeItem('adminAuth')
    setIsAuthenticated(false)
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setFile(file)
      setError(null)
      setSuccess(null)
    }
  }

  const uploadCSV = async () => {
    if (!file) return

    setIsUploading(true)
    setError(null)
    setSuccess(null)

    try {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const csvContent = e.target?.result as string
          console.log('CSV content first line:', csvContent.split('\n')[0])
          
          // Pass the admin client to the import function
          const result = await importHealthData(csvContent, supabaseAdmin)

          if (result.success) {
            if (!result.results?.length) {
              setError('No valid entries found in the CSV file. Please check the file format.')
            } else {
              setSuccess(`Successfully uploaded ${result.results.length} entries!`)
              setFile(null)
              // Clear the file input
              const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
              if (fileInput) {
                fileInput.value = ''
              }
            }
          } else {
            setError(result.error || 'Unknown error occurred')
          }
        } catch (error) {
          console.error('Error processing file:', error)
          setError(error instanceof Error ? error.message : 'Error processing file')
        } finally {
          setIsUploading(false)
        }
      }

      reader.onerror = (error) => {
        console.error('Error reading file:', error)
        setError('Error reading file')
        setIsUploading(false)
      }

      reader.readAsText(file)
    } catch (error) {
      console.error('Error reading file:', error)
      setError(error instanceof Error ? error.message : 'Error reading file')
      setIsUploading(false)
    }
  }

  const handlePoopClick = async () => {
    try {
      setIsPooping(true)
      const { error } = await supabase
        .from('poop_records')
        .insert([{ timestamp: new Date().toISOString() }])

      if (error) throw error

      setMessage('💩 Poop recorded successfully!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Error recording poop:', error)
      setMessage('Error recording poop 😢')
    } finally {
      setIsPooping(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Admin Access
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 
                    border border-gray-700 bg-gray-800 text-gray-300 
                    placeholder-gray-500 focus:outline-none focus:ring-indigo-500 
                    focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Enter admin password"
                />
              </div>
            </div>

            {error && (
              <div className="text-red-500 text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 
                  border border-transparent text-sm font-medium rounded-md 
                  text-white bg-indigo-600 hover:bg-indigo-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 
                  focus:ring-indigo-500"
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-sky-900/10">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              {message && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-slate-300"
                >
                  {message}
                </motion.p>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg 
                hover:bg-gray-700 transition-colors duration-200"
            >
              Logout
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6">
            <h2 className="text-gray-300 text-lg font-medium mb-4">Manual Actions</h2>
            <div className="space-y-4">
              <motion.button
                onClick={handlePoopClick}
                disabled={isPooping}
                className={`w-full px-4 py-2 rounded-lg text-white font-medium
                  bg-gradient-to-r from-amber-600 to-yellow-600
                  hover:from-amber-500 hover:to-yellow-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-lg shadow-amber-500/20
                  transition-all duration-200`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isPooping ? 'Recording...' : '💩 Record Poop'}
              </motion.button>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6">
            <h2 className="text-gray-300 text-lg font-medium mb-4">Upload Health Data</h2>
            <div className="space-y-4">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-indigo-900/50 file:text-indigo-400
                  hover:file:bg-indigo-900/70
                  file:cursor-pointer cursor-pointer"
              />
              <button
                onClick={uploadCSV}
                disabled={!file || isUploading}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg
                  hover:bg-indigo-700 disabled:bg-gray-700 disabled:cursor-not-allowed
                  transition-colors duration-200 flex items-center justify-center"
              >
                {isUploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>

              {error && (
                <div className="text-red-500 text-sm mt-2">
                  {error}
                </div>
              )}

              {success && (
                <div className="text-emerald-500 text-sm mt-2">
                  {success}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
            <h2 className="text-gray-300 text-lg font-medium mb-4">Danger Zone</h2>
            <div className="space-y-4">
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete ALL weight entries? This action cannot be undone!')) {
                    try {
                      const { error } = await supabaseAdmin
                        .from('weight_entries')
                        .delete()
                        .gte('date', '1900-01-01') // This will match all valid dates

                      if (error) throw error

                      setSuccess('Successfully deleted all weight entries!')
                      setError(null)
                    } catch (err) {
                      console.error('Error deleting weight entries:', err)
                      setError(err instanceof Error ? err.message : 'Error deleting weight entries')
                      setSuccess(null)
                    }
                  }
                }}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg
                  hover:bg-red-700 transition-colors duration-200"
              >
                Delete All Weight Entries
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Admin 